import * as React from 'react';
import {
    Box,
    IconButton,
    LinearProgress,
    Link,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DataAPIContext, GET_ONE } from '../api';
import { isOk } from '../utils/errors';
import { ReloadIcon } from './actionIcons';

const PAGE_SIZE = 25;
// Trip Item side panel: toolbar fits in one row only when the viewport is wide enough
const NARROW_TOOLBAR_ROW_MEDIA = '@media (min-width:1100px)';
// Coming back to the page fires both focus and visibilitychange; read the list once.
const REVALIDATE_THROTTLE_MS = 2000;

/**
 * Generic "Used by" panel that shows which records reference the given object.
 *
 * Props:
 *   resource  (string)  – edit API resource name (e.g. "activity")
 *   id        (number|string) – record ID
 *   layout    ('default' | 'narrow' | 'sidebar') – narrow/sidebar: split Type/ID columns for side panels;
 *             narrow: toolbar in one row from 1100px viewport; sidebar: toolbar always stacked (fixed-width drawers)
 */
const UsedByPanel = ({ resource, id, layout = 'default' }) => {
    const isCompact = layout === 'narrow' || layout === 'sidebar';
    const toolbarSingleRowFromWide = layout === 'narrow';
    const dataAPI = React.useContext(DataAPIContext);

    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [page, setPage] = React.useState(0); // DataGrid uses 0-based pages
    const [typeFilter, setTypeFilter] = React.useState('');
    const [search, setSearch] = React.useState('');
    const [searchDebounced, setSearchDebounced] = React.useState('');

    // Debounce search input
    React.useEffect(() => {
        const timer = setTimeout(() => setSearchDebounced(search), 400);
        return () => clearTimeout(timer);
    }, [search]);

    // silent: re-read in the background (no progress bar, a failure keeps the rows shown)
    const fetchData = React.useCallback(
        (refresh = false, silent = false) => {
            if (!id) return;
            if (!silent) setLoading(true);

            const params = new URLSearchParams();
            params.set('page', String(page + 1)); // API uses 1-based pages
            params.set('page_size', String(PAGE_SIZE));
            if (typeFilter) params.set('type', typeFilter);
            if (searchDebounced) params.set('search', searchDebounced);
            if (refresh) params.set('refresh', '1');

            const urlSuffix = `${id}/used_by/?${params.toString()}`;
            const showNothing = () => {
                if (!silent) setData({ count: 0, results: [], available_types: [] });
            };
            dataAPI(GET_ONE, resource, { urlSuffix })
                .then((res) => {
                    // A 4xx/5xx resolves too: its body is not a list.
                    if (isOk(res)) setData(res.data);
                    else showNothing();
                })
                .catch(showNothing)
                .finally(() => {
                    if (!silent) setLoading(false);
                });
        },
        [dataAPI, resource, id, page, typeFilter, searchDebounced],
    );

    // Fetch on mount and when params change
    React.useEffect(() => {
        fetchData();
    }, [fetchData]);

    // CP-7712: the rows link out in a new tab, which is where a notification or ruleset gets
    // edited. Re-read the list when the user comes back instead of showing it as it was left.
    React.useEffect(() => {
        let lastRevalidate = 0;
        const revalidate = () => {
            if (document.visibilityState !== 'visible') return;
            const now = Date.now();
            if (now - lastRevalidate < REVALIDATE_THROTTLE_MS) return;
            lastRevalidate = now;
            fetchData(false, true);
        };
        window.addEventListener('focus', revalidate);
        document.addEventListener('visibilitychange', revalidate);
        return () => {
            window.removeEventListener('focus', revalidate);
            document.removeEventListener('visibilitychange', revalidate);
        };
    }, [fetchData]);

    // Reset page when filters change
    React.useEffect(() => {
        setPage(0);
    }, [typeFilter, searchDebounced]);

    const nameColumn = {
        field: 'description',
        headerName: 'Name',
        flex: 1,
        minWidth: isCompact ? 120 : 160,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
            const link = params.row.dashboard_link;
            if (link) {
                return (
                    <Link href={link} target="_blank" rel="noopener" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {params.value}
                    </Link>
                );
            }
            return params.value;
        },
    };

    const columns = isCompact
        ? [
              {
                  field: 'type_name',
                  headerName: 'Type',
                  width: 100,
                  sortable: false,
                  filterable: false,
              },
              {
                  field: 'id',
                  headerName: 'ID',
                  width: 64,
                  sortable: false,
                  filterable: false,
              },
              nameColumn,
          ]
        : [
              {
                  field: 'type_name',
                  headerName: 'Type',
                  width: 150,
                  sortable: false,
                  filterable: false,
                  renderCell: (params) => `${params.value}.${params.row.id}`,
              },
              nameColumn,
          ];

    const availableTypes = data?.available_types || [];
    const rows = data?.results || [];
    const total = data?.count || 0;

    return (
        <Box sx={{ width: '100%', pt: 1 }}>
            {/* Toolbar: type filter, search, refresh */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 1,
                    mb: 2,
                    alignItems: 'center',
                    flexWrap: isCompact ? 'wrap' : 'nowrap',
                    ...(toolbarSingleRowFromWide && {
                        [NARROW_TOOLBAR_ROW_MEDIA]: { flexWrap: 'nowrap' },
                    }),
                }}
            >
                <TextField
                    select
                    label="Type"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    size="small"
                    sx={
                        isCompact
                            ? {
                                  width: '100%',
                                  ...(toolbarSingleRowFromWide && {
                                      [NARROW_TOOLBAR_ROW_MEDIA]: {
                                          width: 'auto',
                                          minWidth: 120,
                                          flexShrink: 0,
                                      },
                                  }),
                              }
                            : { minWidth: 140 }
                    }
                    variant="outlined"
                >
                    <MenuItem value="">All types</MenuItem>
                    {availableTypes.map((t) => (
                        <MenuItem key={t.key} value={t.key}>
                            {t.name}
                        </MenuItem>
                    ))}
                </TextField>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        width: isCompact ? '100%' : 'auto',
                        flex: 1,
                        minWidth: isCompact ? 0 : 140,
                        ...(toolbarSingleRowFromWide && {
                            [NARROW_TOOLBAR_ROW_MEDIA]: { width: 'auto', minWidth: 0 },
                        }),
                        maxWidth: isCompact ? undefined : 360,
                    }}
                >
                    <TextField
                        label="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        size="small"
                        sx={{ flex: 1, minWidth: 0 }}
                        variant="outlined"
                    />
                    <Tooltip title="Refresh (bypass cache)">
                        <span style={{ flexShrink: 0 }}>
                            <IconButton onClick={() => fetchData(true)} disabled={loading} size="small" sx={{ flexShrink: 0 }}>
                                <ReloadIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {loading && <LinearProgress sx={{ mb: 1 }} />}

            {!loading && rows.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                    No records reference this object.
                </Typography>
            )}

            {(rows.length > 0 || loading) && (
                <DataGrid
                    rows={rows}
                    columns={columns}
                    rowCount={total}
                    pageSize={PAGE_SIZE}
                    page={page}
                    onPageChange={(newPage) => setPage(newPage)}
                    paginationMode="server"
                    disableSelectionOnClick
                    disableColumnMenu
                    autoHeight
                    density="compact"
                    rowsPerPageOptions={[PAGE_SIZE]}
                    getRowId={(row) => `${row.type_key}_${row.id}`}
                    loading={loading}
                    sx={{
                        width: '100%',
                        '& .MuiDataGrid-cell:focus': { outline: 'none' },
                        '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-cell': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                    }}
                />
            )}
        </Box>
    );
};

export default UsedByPanel;
